import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getGroups } from '../api';
import PageHeader from '../components/PageHeader';

export default function Groups() {
  const { data, isLoading } = useQuery({ queryKey: ['groups'], queryFn: getGroups });

  return (
    <>
      <PageHeader
        title="Groups"
        subtitle="Top 2 advance to R32 directly. Best 8 third-place finishers across all groups also advance."
      />
      <div className="px-4 lg:px-0">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="card p-4 h-48 animate-pulse bg-ink-900/60" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data!.groups.map((g) => <GroupCard key={g.label} group={g} />)}
          </div>
        )}
      </div>
    </>
  );
}

function GroupCard({ group }: { group: NonNullable<ReturnType<typeof useQuery<Awaited<ReturnType<typeof getGroups>>>>['data']>['groups'][number] }) {
  return (
    <div className="card p-3 sm:p-4">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-dim">Group</div>
        <div className="font-display text-xl font-semibold">{group.label}</div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-dim border-b border-ink-800">
            <th className="text-left font-medium pb-1.5">Team</th>
            <th className="font-medium text-center w-6">GP</th>
            <th className="font-medium text-center w-6">W</th>
            <th className="font-medium text-center w-6">D</th>
            <th className="font-medium text-center w-6">L</th>
            <th className="font-medium text-center w-8">GD</th>
            <th className="font-medium text-right w-7">Pts</th>
          </tr>
        </thead>
        <tbody>
          {group.teams.map((t, i) => {
            const adv = i < 2;
            const maybe = i === 2;
            return (
              <tr
                key={t.code}
                className={`border-b border-ink-800/40 last:border-0 ${adv ? '' : maybe ? 'opacity-90' : 'opacity-50'}`}
              >
                <td className="py-1.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`inline-block w-1 h-4 rounded-sm ${adv ? 'bg-pitch-500' : maybe ? 'bg-pitch-700/60' : 'bg-ink-700'}`} />
                    <Link to={`/teams/${t.code}`} className="flex items-center gap-1.5 min-w-0 hover:text-pitch-400 transition-colors">
                      <span className="text-base leading-none">{t.flag}</span>
                      <span className="truncate text-xs">{t.code}</span>
                    </Link>
                    {t.owner && (
                      <Link to={`/players/${encodeURIComponent(t.owner)}`} className="text-[9px] uppercase tracking-wider text-dim hover:text-pitch-400">
                        {t.owner}
                      </Link>
                    )}
                  </div>
                </td>
                <td className="stat text-center text-xs">{t.played}</td>
                <td className="stat text-center text-xs">{t.wins}</td>
                <td className="stat text-center text-xs">{t.draws}</td>
                <td className="stat text-center text-xs">{t.losses}</td>
                <td className="stat text-center text-xs">{t.goalDifference > 0 ? `+${t.goalDifference}` : t.goalDifference}</td>
                <td className="stat text-right font-semibold">{t.points}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
